import type { ServiceType } from "@eu.mdotm.api/sphere";
import { hasAccess } from "$root/components/AuthorizationGuard";
import type { UserRole, IUser } from "$root/functional-areas/user";
import type { Intersect, Replace } from "$root/utils";
import { pick } from "@mdotm/utils/objects";
import { type MaybeFn, maybeCall } from "@mdotm/shadcn/legacy/utils";
import { type ComponentPropsWithoutRef, type ReactNode, lazy, useMemo } from "react";
import { useHistory } from "react-router";
import { AS_OF_SEARCH_PARAM, PROPOSAL_SEARCH_PARAM } from "$root/pages/PortfolioDetails/as-of/asOf";
import type { PlatformLayoutOptions } from "./layout-options";

export function extractPath(relativeUrl: string): string {
	return new URL(`http://www.example.com/${relativeUrl.startsWith("/") ? relativeUrl.slice(1) : relativeUrl}`).pathname;
}
export function extractSearch(relativeUrl: string): string {
	return new URL(`http://www.example.com/${relativeUrl.startsWith("/") ? relativeUrl.slice(1) : relativeUrl}`).search;
}
export function extractSearchParams(relativeUrl: string): URLSearchParams {
	return new URL(`http://www.example.com/${relativeUrl.startsWith("/") ? relativeUrl.slice(1) : relativeUrl}`)
		.searchParams;
}

export type RouteOptionsContext = { user: IUser };

export type GuardFn = (
	params: RouteOptionsContext,
) => { passed: true } | { passed: false; reason?: "requires-login" | "missing-permission" };

const alwaysPassGuard: GuardFn = () => ({ passed: true });

export type RouteSerializedProps = { query?: string; params?: Record<string, string> };

export type PropsSerde<T> = {
	serialize: (props: T) => RouteSerializedProps;
	deserialize: (url: string, baseUrl: string) => T;
};

const signInGuard: GuardFn = ({ user }) =>
	user.signedIn ? { passed: true } : { passed: false, reason: "requires-login" };

function requiresServiceGuard(requiredService: ServiceType): GuardFn {
	return ({ user }) =>
		hasAccess(user, { requiredService }) ? { passed: true } : { passed: false, reason: "missing-permission" };
}

function requiresRoleGuard(requiredRole: UserRole): GuardFn {
	return ({ user }) =>
		hasAccess(user, { requiredRole }) ? { passed: true } : { passed: false, reason: "missing-permission" };
}

function andGuards(...guards: GuardFn[]): GuardFn {
	return (params) => {
		for (const guard of guards) {
			const guardResult = guard(params);
			if (!guardResult.passed) {
				return guardResult;
			}
		}
		return { passed: true };
	};
}

function orGuards(...guards: GuardFn[]): GuardFn {
	return (params) => {
		let lastGuardResult: ReturnType<GuardFn> | undefined;
		for (const guard of guards) {
			lastGuardResult = guard(params);
			if (lastGuardResult.passed) {
				return lastGuardResult;
			}
		}
		return lastGuardResult ?? { passed: false };
	};
}

const defaultLayoutOptions: PlatformLayoutOptions = {
	sideBar: true,
	mode: "default",
	maxPageHeight: "auto",
};

export type RouteData<Name extends string, TProps extends Record<string, string>> = {
	name: Name;
	baseUrl: string;
	component: (props: TProps) => ReactNode;
	propsSerde: PropsSerde<TProps>;
	guard: GuardFn;
	layoutOptions: MaybeFn<PlatformLayoutOptions, [RouteOptionsContext]>;
};

function createRoute<Name extends string, TProps extends Record<string, string>>({
	name,
	baseUrl,
	component,
	propsSerde,
	guard,
	layoutOptions,
}: Replace<
	RouteData<Name, TProps>,
	{
		layoutOptions?: MaybeFn<Partial<PlatformLayoutOptions> | null, [RouteOptionsContext]>;
		guard?: GuardFn;
		propsSerde?: PropsSerde<TProps>;
	}
>): Record<Name, RouteData<Name, TProps>> {
	const actualGuard = guard ?? alwaysPassGuard;
	const routeData: RouteData<Name, TProps> = {
		name,
		baseUrl,
		component,
		propsSerde: propsSerde ?? noneSerde(),
		guard: actualGuard,
		layoutOptions: (params: RouteOptionsContext) => {
			const overrides = maybeCall(layoutOptions, params);
			return !overrides
				? defaultLayoutOptions
				: {
						...defaultLayoutOptions,
						...overrides,
					};
		},
	};
	return {
		[name]: routeData,
	} as Record<Name, RouteData<Name, TProps>>;
}

function noneSerde<T extends Record<string, string>>(): PropsSerde<T> {
	return {
		serialize: () => ({}),
		deserialize: () => ({}) as unknown as T,
	};
}

function querySerde<K extends string, T extends [K, ...K[]]>(
	propKeys: T,
): PropsSerde<Partial<Record<T[number], string>>> {
	return {
		serialize: (props) => ({
			query: new URLSearchParams(
				Object.entries(pick(props, propKeys)).filter(([_, value]) => value !== undefined) as [string, string][],
			).toString(),
		}),
		deserialize: (url) => {
			const query = Object.fromEntries(extractSearchParams(url).entries());
			if (query["from"] !== undefined) {
				const { from, ...others } = query;
				let customFrom = from;
				Object.entries(others).forEach(([key, v]) => {
					if (key === "atubm") {
						return;
					}
					customFrom += `&${key}=${v}`;
				});

				return { from: customFrom, ...others } as Record<T[number], string>;
			}

			return query as Record<T[number], string>;
		},
	};
}

function paramsSerde<K extends string, T extends [K, ...K[]]>(propKeys: T): PropsSerde<Record<T[number], string>> {
	return {
		serialize: (props) => {
			return {
				params: pick(props, propKeys),
			};
		},
		deserialize: (url, baseUrl) => {
			const urlParts = extractPath(url).split("/");
			const baseUrlParts = extractPath(baseUrl).split("/");
			const props = {} as Partial<Record<keyof T, string>>;
			for (let i = 0; i < Math.min(urlParts.length, baseUrlParts.length); i++) {
				if (baseUrlParts[i].at(0) === ":") {
					props[baseUrlParts[i].slice(1) as keyof T] = urlParts[i];
				}
			}
			return props as Record<T[number], string>;
		},
	};
}

function multiSerde<T extends [PropsSerde<any>, ...PropsSerde<any>[]]>(
	serdes: T,
): {
	serialize(
		props: Intersect<{ [K in keyof T]: Parameters<T[K]["serialize"]>[0] }[number]>,
	): ReturnType<PropsSerde<any>["serialize"]>;
	deserialize(url: string, baseUrl: string): Intersect<{ [K in keyof T]: ReturnType<T[K]["deserialize"]> }[number]>;
} {
	return {
		serialize: (props) =>
			serdes.reduce((acc, serde) => {
				for (const [key, value] of Object.entries(serde.serialize(props as any))) {
					acc[key] = value;
				}
				return acc;
			}, {} as any),
		deserialize: (url, baseUrl) =>
			serdes.reduce((acc, serde) => {
				for (const [key, value] of Object.entries(serde.deserialize(url, baseUrl))) {
					acc[key] = value;
				}
				return acc;
			}, {} as any),
	};
}

export const routesMap = {
	...createRoute({
		name: "Login",
		baseUrl: "/login",
		component: lazy(() => import("../../pages/Login")),
		propsSerde: querySerde(["from", "atubm"]),
		layoutOptions: {
			sideBar: false,
			maxPageHeight: "screen",
			padding: false,
		},
	}),
	...createRoute({
		name: "AuthCallback",
		baseUrl: "/auth/callback",
		component: lazy(() => import("../../pages/AuthCallback")),
		layoutOptions: {
			sideBar: false,
			maxPageHeight: "screen",
			padding: false,
		},
	}),
	// Same page, for a sign-in against a pool dedicated to an email domain: Cognito redirects
	// to `/auth/callback/<emailDomain>` so the callback names its own pool (see `AuthCallback`).
	// A separate route rather than an optional `:emailDomain?` segment on the one above:
	// `extractPath` runs the base URL through `new URL()`, which would read the `?` as the
	// start of a query string and silently turn the segment into a *required* one.
	...createRoute({
		name: "AuthCallbackForEmailDomain",
		baseUrl: "/auth/callback/:emailDomain",
		component: lazy(() => import("../../pages/AuthCallback")),
		propsSerde: paramsSerde(["emailDomain"]),
		layoutOptions: {
			sideBar: false,
			maxPageHeight: "screen",
			padding: false,
		},
	}),
	// Deep-link entry point for a partner portal: `/sso/<emailDomain>` starts the sign-in for
	// that domain's pool. The portal links here instead of at Cognito so the authorize request
	// is built by the app — with `state`, PKCE, our scopes and the pool's own `redirect_uri`
	// (see `SsoRedirect`). `from` carries the page to land on once signed in.
	...createRoute({
		name: "SsoRedirect",
		baseUrl: "/sso/:emailDomain",
		component: lazy(() => import("../../pages/SsoRedirect")),
		propsSerde: multiSerde([paramsSerde(["emailDomain"]), querySerde(["from"])]),
		layoutOptions: {
			sideBar: false,
			maxPageHeight: "screen",
			padding: false,
		},
	}),
	...createRoute({
		name: "Index",
		baseUrl: "/",
		component: lazy(() => import("../../pages/IndexRedirect")),
		guard: signInGuard,
	}),
	...createRoute({
		name: "MarketDashboard",
		baseUrl: "/dashboard",
		component: lazy(() => import("../../pages/Dashboard")),
		guard: andGuards(signInGuard, requiresServiceGuard("REPORTS")),
	}),
	...createRoute({
		name: "Outlook",
		baseUrl: "/outlook",
		// The page reads its selection off the query string itself (it has to validate the combination
		// against the option tree before trusting it), so these props go unused — the serde is here so
		// the filter and area are addressable through `typedUrlForRoute`, the way AssetClass's are.
		propsSerde: querySerde(["filter", "area"]),
		component: lazy(() => import("../../pages/Outlook")),
		guard: andGuards(signInGuard, requiresServiceGuard("REPORTS")),
	}),
	...createRoute({
		name: "AssetClass",
		baseUrl: "/asset_class",
		propsSerde: querySerde(["filter", "area", "sector"]),
		component: lazy(() => import("../../pages/AssetClass/index")),
		guard: andGuards(signInGuard, requiresServiceGuard("REPORTS")),
	}),
	...createRoute({
		name: "RegimeAnalysisTool",
		baseUrl: "/regime_analysis_tool/:region",
		component: lazy(() => import("../../pages/RegimeAnalysisTool")),
		propsSerde: paramsSerde(["region"]),
		guard: andGuards(signInGuard, requiresServiceGuard("SIGNALS")),
	}),
	...createRoute({
		name: "CustomReports",
		baseUrl: "/custom_reports",
		component: lazy(() => import("../../pages/CustomReports")),
		guard: signInGuard,
	}),
	// ...createRoute({
	// 	name: "PortfolioZipReadyRedirect",
	// 	baseUrl: "/portfolios",
	// 	component: (props) => <Redirect to={typedUrlForRoute("PortfolioStudio/Portfolios", props)} />,
	// 	propsSerde: querySerde(["zipId"]),
	// 	guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	// }),
	...createRoute({
		name: "PortfoliosStudio",
		baseUrl: "/portfolios",
		component: lazy(() => import("../../pages/PortfoliosStudio/index")),
		propsSerde: querySerde(["tab", "status", "zipId", "preselectedInvestments"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "PortfolioStudio/DefaultWorkspace",
		baseUrl: "/portfolio-studio/portfolios",
		propsSerde: querySerde(["preselectedInvestments", "tab"]),
		component: lazy(() => import("../../pages/Workspace/WorkspaceDefault")),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS"), requiresServiceGuard("SMART_WORKSPACE")),
		// layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "PortfolioStudio/Workspace",
		baseUrl: "/portfolio-studio/workspace/:id",
		component: lazy(() => import("../../pages/Workspace/index")),
		propsSerde: multiSerde([paramsSerde(["id"]), querySerde(["preselectedInvestments", "tab"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS"), requiresServiceGuard("SMART_WORKSPACE")),
		// layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "PortfolioLab",
		baseUrl: "/portfolio-studio/lab",
		component: lazy(() => import("../../pages/PortfolioLab")),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS"), requiresServiceGuard("PORTFOLIO_LAB")),
		layoutOptions: {
			// Fixed-height workspace while wide enough for the split view; below the stack breakpoint the
			// panels stack and the page scrolls as a whole (matches the 1151px peek boundary in the CSS).
			maxPageHeight: { capUntilWidth: 1151 },
		},
	}),
	...createRoute({
		name: "Portfolios/ManualPortfolioCreation",
		baseUrl: "/portfolios/manual_creation/portfolio",
		component: lazy(() => import("../../pages/Portfolios/ManualCreation")),
		propsSerde: querySerde(["uuid", "preselectedInvestments", "intentId", "quantityMode"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Portfolios/ManualPortfolioCreationWithWorkspace",
		baseUrl: "/portfolios/:workspaceUid/manual_creation/portfolio",
		component: lazy(() => import("../../pages/Portfolios/ManualCreation")),
		propsSerde: multiSerde([
			paramsSerde(["workspaceUid"]),
			querySerde(["uuid", "preselectedInvestments", "intentId", "quantityMode"]),
		]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Portfolios/ManualCreation",
		baseUrl: "/portfolios/manual_creation/:entity",
		component: lazy(() => import("../../pages/ManualCreation")),
		propsSerde: multiSerde([
			paramsSerde(["entity"]),
			querySerde(["copyComposition", "preselectedTemplate", "intentId"]),
		]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Portfolios/ManualEditPortfolio",
		baseUrl: "/portfolios/manual_edit/portfolio/:uuid",
		component: lazy(() => import("../../pages/Portfolios/ManualEdit")),
		propsSerde: multiSerde([paramsSerde(["uuid"]), querySerde(["copyComposition", "marketDate"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Portfolios/ManualEditPortfolioWithWorkspace",
		baseUrl: "/portfolios/manual_edit/portfolio/:workspaceUid/:uuid",
		component: lazy(() => import("../../pages/Portfolios/ManualEdit")),
		propsSerde: multiSerde([paramsSerde(["workspaceUid", "uuid"]), querySerde(["copyComposition", "marketDate"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Portfolios/ManualEdit",
		baseUrl: "/portfolios/manual_edit/:entity/:uuid",
		component: lazy(() => import("../../pages/ManualCreation/ManualEditComposition")),
		propsSerde: multiSerde([paramsSerde(["entity", "uuid"]), querySerde(["copyComposition"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		/* this creates a proposal */
		name: "Portfolios/EditPortfolio",
		baseUrl: "/portfolios/edit/:portfolioUid",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolio")),
		propsSerde: multiSerde([paramsSerde(["portfolioUid"]), querySerde(["intentId"])]),
		layoutOptions: { maxPageHeight: "screen" },

		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		/* this creates a proposal with workspace context */
		name: "Portfolios/EditPortfolioWithWorkspace",
		baseUrl: "/portfolios/edit/:workspaceUid/:portfolioUid",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolio")),
		propsSerde: multiSerde([paramsSerde(["workspaceUid", "portfolioUid"]), querySerde(["intentId"])]),
		layoutOptions: { maxPageHeight: "screen" },
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		/* this edits the settings without creating a proposal */
		name: "Portfolios/SettingsPortfolio",
		baseUrl: "/portfolios/settings/:portfolioUid",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolio")),
		propsSerde: multiSerde([paramsSerde(["portfolioUid"]), querySerde(["intentId"])]),
		layoutOptions: () => ({ maxPageHeight: "screen" }),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		/* this edits the settings without creating a proposal with workspace context */
		name: "Portfolios/SettingsPortfolioWithWorkspace",
		baseUrl: "/portfolios/settings/:workspaceUid/:portfolioUid",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolio")),
		propsSerde: multiSerde([paramsSerde(["workspaceUid", "portfolioUid"]), querySerde(["intentId"])]),
		layoutOptions: () => ({ maxPageHeight: "screen" }),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "Portfolios/EditPortfolioMulti",
		baseUrl: "/portfolios/edit-multi/:proposalUuid",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolioMulti")),
		propsSerde: multiSerde([paramsSerde(["proposalUuid"]), querySerde(["intentId"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "Portfolios/EditPortfolioMultiWithWorkspace",
		baseUrl: "/portfolios/edit-multi/:workspaceUid/:proposalUuid",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolioMulti")),
		propsSerde: multiSerde([paramsSerde(["workspaceUid", "proposalUuid"]), querySerde(["intentId"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "Portfolios/SettingsPortfolioMulti",
		baseUrl: "/portfolios/settings-multi/:bulkUid",
		component: lazy(() => import("../../pages/Portfolios/SettingsPortfolioMulti")),
		propsSerde: paramsSerde(["bulkUid"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "Portfolios/SettingsPortfolioMultiWithWorkspace",
		baseUrl: "/portfolios/settings-multi/:workspaceUid/:bulkUid",
		component: lazy(() => import("../../pages/Portfolios/SettingsPortfolioMulti")),
		propsSerde: multiSerde([paramsSerde(["workspaceUid", "bulkUid"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "Portfolios/CreatePortfolio",
		baseUrl: "/portfolios/new",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolio")),
		propsSerde: querySerde(["intentId"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Portfolios/Draft",
		baseUrl: "/portfolios/draft/:portfolioUid",
		component: lazy(() => import("../../pages/Portfolios/CreatePortfolio")),
		propsSerde: multiSerde([paramsSerde(["portfolioUid"]), querySerde(["intentId"])]),

		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "PortfolioDetails",
		baseUrl: "/portfolio_details/:portfolioUid",
		component: lazy(() => import("../../pages/PortfolioDetails")),
		propsSerde: multiSerde([
			paramsSerde(["portfolioUid"]),
			querySerde([PROPOSAL_SEARCH_PARAM, "tab", AS_OF_SEARCH_PARAM]),
		]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "PortfolioDetailsWithWorkspace",
		baseUrl: "/portfolio_details/:workspaceUid/:portfolioUid",
		component: lazy(() => import("../../pages/PortfolioDetails")),
		propsSerde: multiSerde([
			paramsSerde(["workspaceUid", "portfolioUid"]),
			querySerde([PROPOSAL_SEARCH_PARAM, "tab", AS_OF_SEARCH_PARAM]),
		]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "ReviewInstruments",
		baseUrl: "/:entity/review_composition/:uuid",
		component: lazy(() => import("../../pages/ReviewInstruments")),
		propsSerde: paramsSerde(["uuid", "entity"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "ReviewInstrumentsWithWorkspace",
		baseUrl: "/:entity/review_composition/:workspaceUid/:uuid",
		component: lazy(() => import("../../pages/ReviewInstruments")),
		propsSerde: paramsSerde(["workspaceUid", "uuid", "entity"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "ReferenceDetails",
		baseUrl: "/reference_details/:uuid",
		component: lazy(() => import("../../pages/Reference")),
		propsSerde: multiSerde([paramsSerde(["uuid"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "UniverseDetails",
		baseUrl: "/universe_details/:universeUuid",
		component: lazy(() => import("../../pages/UniverseDetails")),
		propsSerde: paramsSerde(["universeUuid"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "UniverseWithGroupsDetails",
		baseUrl: "/universe_with_groups/:universeUuid",
		component: lazy(() => import("../../pages/UniverseDetails/UniverseWithGroup")),
		propsSerde: paramsSerde(["universeUuid"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS"), requiresServiceGuard("GROUPS_UNIVERSE")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "EditUniverseWithGroups",
		baseUrl: "/universe_with_groups_editor/:universeUuidOrNew",
		component: lazy(() => import("../../pages/UniverseDetails/NewOrEditUniverseWithGroup")),
		propsSerde: multiSerde([paramsSerde(["universeUuidOrNew"]), querySerde(["intentId", "selectedGroupUuid"])]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS"), requiresServiceGuard("GROUPS_UNIVERSE")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	// The market view workspace does three jobs, and each gets its own route and its own component.
	// It used to be one `:action/:uuid/:type` route behind one component, so the props were the union
	// of all three jobs' needs with everything optional, and the component re-derived its mode from
	// them. `type` and `custom` in particular are properties OF a market view rather than coordinates
	// for finding one, so requiring them in the URL meant callers that didn't happen to know them
	// (shared-entity toasts, notifications, cross-entity links) passed `type: ""` and landed on a
	// broken page, or suppressed the link entirely.
	...createRoute({
		name: "MarketViewWorkSpace/View",
		baseUrl: "/workspace_market/view/:uuid",
		component: lazy(() => import("../../pages/MarketViewWorkSpace/ViewMarketView")),
		propsSerde: paramsSerde(["uuid"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "MarketViewWorkSpace/Clone",
		baseUrl: "/workspace_market/clone/:uuid",
		component: lazy(() => import("../../pages/MarketViewWorkSpace/CloneMarketView")),
		propsSerde: paramsSerde(["uuid"]),
		guard: andGuards(signInGuard, requiresServiceGuard("INVESTMENTS")),
	}),
	...createRoute({
		name: "MarketViewWorkSpace/New",
		baseUrl: "/workspace_market/:type",
		component: lazy(() => import("../../pages/MarketViewWorkSpace/NewMarketView")),
		propsSerde: multiSerde([paramsSerde(["type"]), querySerde(["isCustom", "intentId"])]),
		guard: signInGuard,
	}),
	...createRoute({
		name: "PortfolioStudioSettings",
		baseUrl: "/portfolio_studio_settings",
		component: lazy(() => import("../../pages/PortfolioStudioSettings")),
		propsSerde: querySerde(["tab", "uuid"]),
		guard: andGuards(
			signInGuard,
			orGuards(
				requiresServiceGuard("CUSTOM_MARKET_VIEW_SETTINGS_EDITOR"),
				requiresServiceGuard("INVESTMENTS_REPORT_TEMPLATE_EDITOR"),
			),
		),
	}),
	...createRoute({
		name: "PortfolioStudioSettings/ReportEditor",
		baseUrl: "/portfolio_studio_settings/report-editor/:reportUid",
		component: lazy(() => import("../../pages/PortfolioStudioSettings/ReportEditor")),
		propsSerde: paramsSerde(["reportUid"]),
		guard: signInGuard,
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "PortfolioStudioSettings/InstrumentCustomisation",
		baseUrl: "/portfolio_studio_settings/instrument_customisation",
		component: lazy(() => import("../../pages/PortfolioStudioSettings/InstrumentCustomisationSetting")),
		propsSerde: querySerde(["universeUuid", "universeName", "basketType"]),
		guard: signInGuard,
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Notification",
		baseUrl: "/notification_settings",
		component: lazy(() => import("../../pages/Notification")),
		guard: andGuards(signInGuard, orGuards(requiresServiceGuard("INVESTMENTS"), requiresServiceGuard("REPORTS"))),
	}),
	...createRoute({
		name: "Report",
		baseUrl: "/report/:templateId",
		component: lazy(() => import("../../pages/Report")),
		propsSerde: multiSerde([
			paramsSerde(["templateId"]),
			querySerde(["variant", "objectId", "status", "enhanced", "autoScroll", "historyEventUuid", "asOfDate"]),
		]),
		guard: signInGuard,
		layoutOptions: {
			mode: "print",
			sideBar: false,
		},
	}),
	...createRoute({
		name: "Report/Editor",
		baseUrl: "/report_editor/:customerId",
		component: lazy(() => import("../../pages/Report/ReportEditor")),
		propsSerde: paramsSerde(["customerId"]),
		guard: signInGuard,
		layoutOptions: {
			mode: "print",
			sideBar: false,
		},
	}),
	...createRoute({
		name: "Report/Demo",
		baseUrl: "/report/:templateId",
		component: lazy(() => import("../../pages/Report")),
		propsSerde: paramsSerde(["templateId"]),
		guard: signInGuard,
		layoutOptions: {
			mode: "print",
			sideBar: false,
		},
	}),
	...createRoute({
		name: "LegacyBenchmark",
		baseUrl: "/legacy_benchmarks/:uuid",
		component: lazy(() => import("../../pages/LegacyBenchmark")),
		propsSerde: paramsSerde(["uuid"]),
		guard: signInGuard,
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "AdvanceSettings/UserSettingsPanel",
		baseUrl: "/advance_settings/settings_panel",
		component: lazy(() => import("../../pages/AdvanceSettings/UserSettingsPanel")),
		guard: andGuards(signInGuard, requiresRoleGuard("ROOT")),
	}),
	...createRoute({
		name: "AdvanceSettings/PlatformInfo",
		baseUrl: "/advance_settings/platform_info",
		component: lazy(() => import("../../pages/AdvanceSettings/PlatformInfo")),
		guard: signInGuard,
	}),
	...createRoute({
		name: "Maintenance",
		baseUrl: "/maintenance",
		component: lazy(() => import("../../pages/Maintenance")),
		layoutOptions: {
			mode: "print",
			sideBar: false,
		},
	}),
	...createRoute({
		name: "Storyfolio/Details",
		baseUrl: "/storyfolio/details/:uuid",
		component: lazy(() => import("../../pages/Storyfolio/StoryfolioDetails")),
		propsSerde: paramsSerde(["uuid"]),
		guard: andGuards(signInGuard, requiresServiceGuard("COMMENTARY_BUILDER")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Storyfolio/Creation",
		baseUrl: "/storyfolio/new",
		component: lazy(() => import("../../pages/Storyfolio/StoryfolioCreation")),
		guard: andGuards(signInGuard, requiresServiceGuard("COMMENTARY_BUILDER")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Storyfolio/Studio",
		baseUrl: "/storyfolio",
		component: lazy(() => import("../../pages/Storyfolio")),
		guard: andGuards(signInGuard, requiresServiceGuard("COMMENTARY_BUILDER")),
		layoutOptions: { maxPageHeight: "screen" },
	}),
	...createRoute({
		name: "Page404",
		baseUrl: "*",
		component: lazy(() => import("../../pages/404")),
		layoutOptions: {
			mode: "print",
			sideBar: false,
		},
	}),
};

export type RouteName = keyof typeof routesMap;
type PropsForRouteHelper<K extends RouteName> = ComponentPropsWithoutRef<Awaited<(typeof routesMap)[K]["component"]>>;
export type PropsForRoute<K extends RouteName> = PropsForRouteHelper<K> extends Record<string, string>
	? PropsForRouteHelper<K>
	: null;

function typedNavigation<K extends RouteName>(baseFn: (url: string) => void, target: K, props: PropsForRoute<K>): void {
	baseFn(typedUrlForRoute(target, props));
}

export function typedUrlForRoute<K extends RouteName>(target: K, props: PropsForRoute<K>): string {
	const serializedProps = props ? routesMap[target].propsSerde.serialize(props as any) : {};
	return (
		Object.entries(serializedProps.params ?? {}).reduce(
			(url, [key, value]) => url.replace(`:${key}`, value),
			routesMap[target].baseUrl,
		) +
		`${serializedProps.query ? `${routesMap[target].baseUrl.includes("?") ? "&" : "?"}${serializedProps.query}` : ""}`
	);
}

export function typedPropsFromUrl<K extends RouteName>(target: K, url: string): PropsForRoute<K> {
	return routesMap[target].propsSerde.deserialize(url, routesMap[target].baseUrl) as PropsForRoute<K>;
}

export function useTypedNavigation(): {
	push<K extends RouteName>(target: K, props: PropsForRoute<K>): void;
	replace<K extends RouteName>(target: K, props: PropsForRoute<K>): void;
} {
	const { push, replace } = useHistory();
	return useMemo(
		() => ({
			push: (target, props) => typedNavigation(push, target, props),
			replace: (target, props) => typedNavigation(replace, target, props),
		}),
		[push, replace],
	);
}
/* sideBarOptions: (params: RouteOptionsContext) => {
			const overrides = maybeCall(sideBarOptions, params);
			return !overrides
				? null
				: {
						subMenuItems: null,
						...overrides,
						show: actualGuard(params).passed && overrides.show !== false,
				  };
		}, */
